'use client';

import {
  HTMLAttributes,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import Image from 'next/image';
import useEmblaCarousel from 'embla-carousel-react';
import cx from 'classnames';
import arrowIcon from '../assets/icons/arrow.svg';

interface CarouselProps {
  className?: HTMLAttributes<HTMLDivElement>['className'];
  group?: boolean;
  background?: 'WHITE' | 'BLUE';
  xlStartIndex?: number;
}

export const Carousel: React.FC<PropsWithChildren<CarouselProps>> = ({
  children,
  className,
  group = false,
  background,
  xlStartIndex = 0,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    breakpoints: {
      '(min-width: 1280px)': {
        ...(group ? { slidesToScroll: 3 } : {}),
        startIndex: xlStartIndex,
      },
    },
  });

  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi],
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnEnabled(emblaApi.canScrollPrev());
    setNextBtnEnabled(emblaApi.canScrollNext());
    setScrollSnaps(emblaApi.scrollSnapList());
  }, [emblaApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, setScrollSnaps, onSelect]);

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div
          className={cx(
            'grid grid-flow-col auto-cols-[80%] sm:auto-cols-[60%] md:auto-cols-[40%] lg:auto-cols-[33%] xl:auto-cols-[25%] gap-20',
            className,
          )}
        >
          {children}
        </div>
      </div>

      {prevBtnEnabled && (
        <button
          onClick={scrollPrev}
          className={cx(
            'group absolute top-0 bottom-0 left-0 bg-gradient-to-r to-transparent sm:pr-10',
            background === 'BLUE' ? 'from-blue-50' : ' from-white',
          )}
          aria-label="previous"
        >
          <div className="-rotate-180 w-11 h-11 bg-white rounded-full border-[#111827] border border-solid flex justify-center items-center ml-9">
            <Image src={arrowIcon} alt="previous" />
          </div>
        </button>
      )}
      {nextBtnEnabled && (
        <button
          onClick={scrollNext}
          className={cx(
            'group absolute top-0 bottom-0 right-0 bg-gradient-to-l to-transparent sm:pl-10',
            background === 'BLUE' ? 'from-blue-50' : ' from-white',
          )}
          aria-label="next"
        >
          <div className="w-11 h-11 bg-white rounded-full border-[#111827] border border-solid flex justify-center items-center mr-9">
            <Image src={arrowIcon} alt="next" />
          </div>
        </button>
      )}
      {scrollSnaps.length > 1 ? (
        <div className="hidden sm:flex justify-center space-x-3 m-6">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              className={cx(
                'w-2 h-2 rounded-full',
                index === selectedIndex ? 'bg-black' : 'bg-gray-500',
              )}
              onClick={() => scrollTo(index)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};
